// Enhanced rate limiting hook with multiple strategies
import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';

interface RateLimitConfig {
  maxAttempts: number;
  timeWindow: number; // in milliseconds
  blockDuration?: number; // in milliseconds
  warningThreshold?: number; // attempts before warning
}

interface RateLimitState {
  attempts: number;
  lastAttempt: number;
  blocked: boolean;
  blockedUntil?: number;
}

const defaultConfigs: Record<string, RateLimitConfig> = {
  login: { maxAttempts: 5, timeWindow: 15 * 60 * 1000, blockDuration: 30 * 60 * 1000, warningThreshold: 3 },
  search: { maxAttempts: 50, timeWindow: 60 * 1000, warningThreshold: 40 },
  api: { maxAttempts: 100, timeWindow: 60 * 1000, warningThreshold: 80 },
  payment: { maxAttempts: 3, timeWindow: 15 * 60 * 1000, blockDuration: 60 * 60 * 1000, warningThreshold: 2 },
  contact: { maxAttempts: 5, timeWindow: 60 * 60 * 1000, blockDuration: 24 * 60 * 60 * 1000, warningThreshold: 3 },
  admin_action: { maxAttempts: 10, timeWindow: 5 * 60 * 1000, blockDuration: 15 * 60 * 1000, warningThreshold: 7 }
};

export const useRateLimit = (action: string, customConfig?: Partial<RateLimitConfig>) => {
  const config = { ...defaultConfigs[action], ...customConfig };
  const stateRef = useRef<RateLimitState>({
    attempts: 0,
    lastAttempt: 0,
    blocked: false
  });

  const [isBlocked, setIsBlocked] = useState(false);

  // Helper function to format time in a user-friendly way
  const formatWaitTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} שניות`;
    } else {
      const minutes = Math.ceil(seconds / 60);
      return `${minutes} דקות`;
    }
  };

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    const state = stateRef.current;

    // Check for development environment
    const isDevelopment = import.meta.env.DEV ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (isDevelopment) {
      return true;
    }

    // Check if still blocked
    if (state.blocked && state.blockedUntil && now < state.blockedUntil) {
      const remainingSeconds = Math.ceil((state.blockedUntil - now) / 1000);
      const formattedTime = formatWaitTime(remainingSeconds);
      toast.error("פעולה חסומה", {
        description: `נא להמתין ${formattedTime} לפני ניסיון נוסף`,
      });
      return false;
    }

    // Reset if time window has passed
    if (now - state.lastAttempt > config.timeWindow) {
      state.attempts = 0;
      state.blocked = false;
      state.blockedUntil = undefined;
      setIsBlocked(false);
    }

    // Check if limit exceeded
    if (state.attempts >= config.maxAttempts) {
      if (config.blockDuration) {
        state.blocked = true;
        state.blockedUntil = now + config.blockDuration;
        setIsBlocked(true);

        toast.error("חריגה ממגבלת הפעולות", {
          description: `חסימה זמנית של ${Math.ceil(config.blockDuration / 60000)} דקות`,
        });
      } else {
        toast.error("חריגה ממגבלת הפעולות", {
          description: "נא המתן לפני ביצוע פעולה נוספת",
        });
      }
      return false;
    }

    // Warning threshold
    if (config.warningThreshold && state.attempts >= config.warningThreshold) {
      const remaining = config.maxAttempts - state.attempts;
      toast.error("התראת מגבלת פעולות", {
        description: `נותרו ${remaining} ניסיונות לפני חסימה זמנית`,
      });
    }

    return true;
  }, [action, config, toast]);

  const recordAttempt = useCallback((success: boolean = false) => {
    const now = Date.now();
    const state = stateRef.current;

    state.lastAttempt = now;

    if (!success) {
      state.attempts += 1;
    } else {
      // Successful attempts can reduce the count slightly
      state.attempts = Math.max(0, state.attempts - 1);
    }

    // Store in localStorage for persistence across sessions
    try {
      const storageKey = `rateLimit_${action}`;
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to persist rate limit state:', error);
    }
  }, [action]);

  // Load persisted state on first use
  const loadPersistedState = useCallback(() => {
    try {
      const storageKey = `rateLimit_${action}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedState = JSON.parse(stored) as RateLimitState;
        const now = Date.now();

        // Check if stored data is still valid
        if (now - parsedState.lastAttempt < config.timeWindow * 2) {
          stateRef.current = parsedState;

          // Update blocked state if needed
          if (parsedState.blocked && parsedState.blockedUntil && now < parsedState.blockedUntil) {
            setIsBlocked(true);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted rate limit state:', error);
    }
  }, [action, config.timeWindow]);

  // Initialize on first render
  useState(() => {
    loadPersistedState();
  });

  const resetLimit = useCallback(() => {
    stateRef.current = {
      attempts: 0,
      lastAttempt: 0,
      blocked: false
    };
    setIsBlocked(false);

    try {
      const storageKey = `rateLimit_${action}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear rate limit state:', error);
    }
  }, [action]);

  const getRemainingAttempts = useCallback((): number => {
    return Math.max(0, config.maxAttempts - stateRef.current.attempts);
  }, [config.maxAttempts]);

  const getTimeUntilReset = useCallback((): number => {
    const state = stateRef.current;
    if (state.blocked && state.blockedUntil) {
      return Math.max(0, state.blockedUntil - Date.now());
    }
    return Math.max(0, config.timeWindow - (Date.now() - state.lastAttempt));
  }, [config.timeWindow]);

  return {
    checkRateLimit,
    recordAttempt,
    resetLimit,
    isBlocked,
    getRemainingAttempts,
    getTimeUntilReset,
    config
  };
};